/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FC, useContext, useMemo } from 'react';

import { Table as TableComponent } from '@/components/blocks/Table/Table';
import { TableBody } from '@/components/blocks/Table/TableBody';
import { TableHead } from '@/components/blocks/Table/TableHead';
import { TablePagination } from '@/components/blocks/TablePagination';
import { OrderItemTableHeaderRow } from '@/components/content/OrderItemTable/parts/HeaderRow';
import { OrderItemTableRow } from '@/components/content/OrderItemTable/parts/Row';
import { useOrderItemTable } from '@/data/Content/OrderItemTable';
import { PAGINATION } from '@/data/constants/tablePagination';
import { ContentContext } from '@/data/context/content';
import {
	VisibilityState,
	getCoreRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table';
import {
	getMultiFreeGiftPromoMap,
	useMultiFreeGiftPromoDetails,
} from '@/data/Content/MultiFreeGiftPromoDetails';
import { useHasFreeGiftPromoIconPredicate } from '@/data/utils/usePromoIcons';
import { SearchProfiles } from '@/data/constants/commonIntegrationConstants';
import { useProducts } from '@/data/Content/Products';
import { ProductType } from '@/data/types/Product';
import { MultiFreeGiftPromoContext } from '@/components/blocks/PLPProductCard/context/MultiFreeGiftPromoContext';
import { keyBy } from 'lodash';

type ArrayType<T> = T extends (infer Item)[] ? Item : T;
type OrderItemTableData = ReturnType<typeof useOrderItemTable>['data'];
export type OrderItemTableRowData = ArrayType<OrderItemTableData>;

type Props = {
	columns: {
		header: string;
		accessorKey: string;
		id?: string;
	}[];
	data: OrderItemTableData;
};

const hiddenColumns: Record<string, boolean> = { availability: true, quantity: true, price: true };

export const OrderItemTable: FC<Props> = ({ columns, data }) => {
	const { view, pageCount, pagination, setPagination, totalRecords } = useContext(
		ContentContext
	) as ReturnType<typeof useOrderItemTable> & {
		view: string;
	};
	const columnVisibility = useMemo<VisibilityState>(
		() =>
			columns.reduce((acc, { accessorKey, id }) => {
				const key = id || accessorKey;
				return view !== 'full' && hiddenColumns[key] ? { ...acc, [key]: false } : acc;
			}, {}),
		[columns, view]
	);
	const {
		getHeaderGroups,
		getState,
		setPageSize,
		setPageIndex: gotoPage,
		getCanPreviousPage,
		getCanNextPage,
		nextPage,
		previousPage,
		getPageCount,
		getRowModel,
	} = useReactTable({
		columns,
		data,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		manualPagination: true,
		state: { pagination, columnVisibility },
		pageCount,
		initialState: {
			pagination,
			columnVisibility,
		},
		onPaginationChange: setPagination,
	});

	const paginationComponentProps = {
		canPreviousPage: getCanPreviousPage(),
		canNextPage: getCanNextPage(),
		pageCount: getPageCount(),
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		pageIndex: getState().pagination.pageIndex,
		pageSize: getState().pagination.pageSize,
	};
	const hasFreeGiftIcon = useHasFreeGiftPromoIconPredicate();
	const partNumbers = useMemo(() => data.map((p) => p?.itemDetails?.partNumber), [data]);

	const {
		products: productsInOrder,
		rawData: rawProductsInOrder,
		loading: isProductsLoading,
	} = useProducts({
		id: partNumbers,
		condition: partNumbers.length > 0,
		profileName: SearchProfiles.HDM_FIND_PRODUCTS_CART_LITE_BY_SKU,
	});

	const productsByPartNumber = useMemo(
		() =>
			keyBy(
				(rawProductsInOrder ?? [])
					.map(({ product }) => product)
					.filter((product): product is ProductType => Boolean(product)),
				'partNumber'
			),
		[rawProductsInOrder]
	);

	const pgCatentryIds = useMemo(() => {
		if (!isProductsLoading && productsInOrder) {
			return productsInOrder
				.filter((p) => (p ? hasFreeGiftIcon(p) : false))
				.map((p) => p?.id)
				.join(',');
		}
		return '';
	}, [productsInOrder, hasFreeGiftIcon, isProductsLoading]);
	const { data: batchedPgPromos, loading: pgPromosLoading } =
		useMultiFreeGiftPromoDetails(pgCatentryIds);
	const pgPromoMap = useMemo(() => {
		if (pgPromosLoading) {
			return new Map<string, string>();
		}
		return getMultiFreeGiftPromoMap(batchedPgPromos);
	}, [batchedPgPromos, pgPromosLoading]);

	return (
		<>
			<MultiFreeGiftPromoContext.Provider value={pgPromoMap}>
				<TableComponent
					id="order-item-table"
					data-testid="order-item-table"
					{...(view !== 'full' ? { role: 'presentation' } : {})}
				>
					{view === 'full' ? (
						<TableHead>
							{getHeaderGroups().map((headerGroup, i) => (
								<OrderItemTableHeaderRow
									key={`OrderItemTableHeaderRow-${i}`}
									headerGroup={headerGroup}
								/>
							))}
						</TableHead>
					) : null}
					<TableBody>
						{getRowModel().rows.map((row) => (
							<OrderItemTableRow
								key={`order-item-table-row-${row.id}`}
								row={row}
								lastRow={row.id === (totalRecords - 1).toString()}
								product={productsByPartNumber[row.original.itemDetails.partNumber]}
								productsLoading={isProductsLoading}
							/>
						))}
					</TableBody>
				</TableComponent>
			</MultiFreeGiftPromoContext.Provider>
			{view !== 'mini' && totalRecords > PAGINATION.sizes[0] ? (
				<TablePagination {...paginationComponentProps} />
			) : null}
		</>
	);
};
